import axios from "axios";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { STATUSES } from "./apiStatus";
import { base_url } from "../utils";
import { getAccesstoken } from "../helpers/utils";

const api = axios.create({ baseURL: `${base_url}/digilocker` });
const CACHE_TTL = 2 * 60 * 1000;

const readCache = (key) => {
  try {
    const cached = JSON.parse(sessionStorage.getItem(key));
    if (cached?.data && Date.now() - cached.savedAt < CACHE_TTL) {
      return cached.data;
    }
    sessionStorage.removeItem(key);
  } catch {
    sessionStorage.removeItem(key);
  }
  return null;
};

const writeCache = (key, data) => {
  try {
    sessionStorage.setItem(key, JSON.stringify({ savedAt: Date.now(), data }));
  } catch {
    // Requests continue normally when browser storage is unavailable.
  }
};

export const invalidateReportsCache = () => {
  try {
    Object.keys(sessionStorage)
      .filter((key) => key.startsWith("digilocker_report"))
      .forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // No action is required when browser storage is unavailable.
  }
};

const authConfig = (config = {}) => ({
  ...config,
  headers: {
    ...config.headers,
    Authorization: `Bearer ${getAccesstoken()}`,
  },
});

const errorMessage = (error, fallback) =>
  error.response?.data?.message || error.message || fallback;

const splitDocuments = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value !== "string") return [];
  return value
    .split(",")
    .map((document) => document.trim())
    .filter(Boolean);
};

const legacyRecordDetail = (record) => {
  const requestedDocuments = splitDocuments(record.documents_required);
  const isSuccessful = String(record.is_success || "").toLowerCase() === "yes";

  return {
    requested_at: record.date_time,
    requested_documents: requestedDocuments,
    requested_documents_count: requestedDocuments.length,
    // The legacy list response does not contain the structured document payload
    // required by the document viewer. Keep the row useful without exposing an
    // action that would call an endpoint unavailable on legacy deployments.
    fetched_documents_count: isSuccessful ? requestedDocuments.length : 0,
    status: isSuccessful ? "complete" : "initiated",
    documents_available: false,
  };
};

export const normalizeReportResponse = (payload, params = {}) => {
  const nestedData = payload?.data;
  const isLegacyResponse = Array.isArray(nestedData);
  const reportData = isLegacyResponse ? payload : nestedData || payload || {};
  const rawRecords = isLegacyResponse
    ? nestedData
    : reportData.records || (Array.isArray(reportData.data) ? reportData.data : []);
  const records = rawRecords.map((record) => ({
    ...record,
    transaction_id:
      record.transaction_id || record.state_value || record.client_token || String(record.id),
    detail: record.detail || legacyRecordDetail(record),
  }));
  const rawPagination = reportData.pagination || {};
  const page = Number(rawPagination.page ?? reportData.page ?? params.page ?? 1);
  const perPage = Number(
    rawPagination.per_page ??
      reportData.per_page ??
      params.per_page ??
      (records.length || 10)
  );
  const totalRecords = Number(
    rawPagination.total_records ?? rawPagination.total ?? reportData.total ?? records.length
  );
  const totalPages = Number(
    rawPagination.total_pages ?? rawPagination.pages ?? reportData.pages ??
      (totalRecords ? Math.ceil(totalRecords / perPage) : 0)
  );

  return {
    records,
    pagination: {
      page,
      per_page: perPage,
      total_records: totalRecords,
      total_pages: totalPages,
      from: totalRecords && records.length ? (page - 1) * perPage + 1 : 0,
      to: totalRecords && records.length ? (page - 1) * perPage + records.length : 0,
      has_previous: page > 1,
      has_next: page < totalPages,
      previous_page: page > 1 ? page - 1 : null,
      next_page: page < totalPages ? page + 1 : null,
      ...rawPagination,
    },
  };
};

export const reportData = createAsyncThunk(
  "report/fetch",
  async (request = {}, { rejectWithValue }) => {
    const controlledRequest = Object.prototype.hasOwnProperty.call(
      request,
      "params"
    );
    const params = controlledRequest ? request.params : request;
    const forceRefresh = controlledRequest && request.forceRefresh;
    const cacheKey = `digilocker_reports_v7_${JSON.stringify(params)}`;

    try {
      if (!forceRefresh) {
        const cached = readCache(cacheKey);
        if (cached) return cached;
      }
      const response = await api.get(
        "/aadhar-records",
        authConfig({ params: forceRefresh ? { ...params, refresh: 1 } : params })
      );
      const normalized = normalizeReportResponse(response.data, params);
      writeCache(cacheKey, normalized);
      return normalized;
    } catch (error) {
      return rejectWithValue(errorMessage(error, "Unable to fetch reports"));
    }
  }
);

export const getReportExportCount = async (filters) => {
  try {
    const response = await api.post(
      "/aadhar-records/export/count",
      { filters },
      authConfig()
    );
    return response.data;
  } catch (error) {
    throw new Error(errorMessage(error, "Unable to check matching records"));
  }
};

export const prepareReportExport = async (filters, startRecord, endRecord) => {
  try {
    const response = await api.post(
      "/aadhar-records/export",
      { filters, start_record: startRecord, end_record: endRecord },
      authConfig()
    );
    return response.data;
  } catch (error) {
    throw new Error(errorMessage(error, "Unable to prepare the Excel file"));
  }
};

export const getReportExportChunk = async (exportId, chunk) => {
  try {
    const response = await api.get(
      `/aadhar-records/export/${encodeURIComponent(exportId)}/chunk`,
      authConfig({ params: { chunk } })
    );
    return response.data;
  } catch (error) {
    throw new Error(errorMessage(error, "Unable to continue the download"));
  }
};

export const finishReportExport = async (exportId) => {
  if (!exportId) return;
  try {
    await api.delete(
      `/aadhar-records/export/${encodeURIComponent(exportId)}`,
      authConfig()
    );
  } catch {
    // Temporary download data expires automatically if cleanup is interrupted.
  }
};

export const getReportDetail = async (recordId, forceRefresh = false) => {
  const cacheKey = `digilocker_report_detail_v4_${recordId}`;
  try {
    if (!forceRefresh) {
      const cached = readCache(cacheKey);
      if (cached) return cached;
    }
    const response = await api.get(
      `/aadhar-records/${recordId}`,
      authConfig()
    );
    writeCache(cacheKey, response.data.data);
    return response.data.data;
  } catch (error) {
    throw new Error(
      errorMessage(error, "Unable to load transaction details")
    );
  }
};

export const getTransactionDocuments = async (transactionId, forceRefresh = false) => {
  const cacheKey = `digilocker_report_documents_v4_${transactionId}`;
  try {
    if (!forceRefresh) {
      const cached = readCache(cacheKey);
      if (cached) return cached;
    }
    const response = await api.get(
      `/aadhar-records/transaction/${encodeURIComponent(transactionId)}/documents`,
      authConfig()
    );
    writeCache(cacheKey, response.data.data);
    return response.data.data;
  } catch (error) {
    throw new Error(
      errorMessage(error, "Unable to load transaction documents")
    );
  }
};

const initialState = {
  records: [],
  pagination: {
    page: 1,
    per_page: 10,
    total_records: 0,
    total_pages: 0,
    from: 0,
    to: 0,
    has_previous: false,
    has_next: false,
    previous_page: null,
    next_page: null,
  },
  status: STATUSES.IDLE,
  error: null,
};

const reportSlice = createSlice({
  name: "report",
  initialState,
  reducers: {
    clearReportData: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(reportData.pending, (state) => {
        state.status = STATUSES.LOADING;
        state.error = null;
      })
      .addCase(reportData.fulfilled, (state, action) => {
        state.records = action.payload?.records || [];
        state.pagination = {
          ...initialState.pagination,
          ...action.payload?.pagination,
        };
        state.status = STATUSES.IDLE;
      })
      .addCase(reportData.rejected, (state, action) => {
        state.status = STATUSES.ERROR;
        state.error = action.payload;
      });
  },
});

export const { clearReportData } = reportSlice.actions;
export default reportSlice.reducer;
